import React, { useEffect, useState } from 'react'
import {
  CButton,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from '@coreui/react-pro'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { GripVertical } from 'lucide-react'
import CIcon from '@coreui/icons-react'
import { cilMinus } from '@coreui/icons'
import { toast } from 'react-toastify'

import ToastComponent from 'src/components/common/TaostComponent.js'
import TeamPlayerService from 'src/service/TeamPlayerService'

const SquadPickedTable = ({ pickedPlayer, fixtureId = 0, teamId = 0, onChanged }) => {
  const [players, setPlayers] = useState([])
  // The row whose remove request is still in flight.
  const [pendingId, setPendingId] = useState(null)

  useEffect(() => {
    setPlayers(pickedPlayer || [])
  }, [pickedPlayer])

  // Reorder function
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  // Save order to backend
  const savePlayerOrder = async (updatedList) => {
    const payload = {
      fixtureId,
      teamId,
      playerOrder: updatedList.map((p, index) => ({
        id: p.id,
        orderno: index + 1,
      })),
    }

    try {
      const res = await TeamPlayerService.savePlayerOrder(payload)
      if (res.status === 200) {
        ToastComponent('Player order saved successfully', 'success')
      }
    } catch (err) {
      ToastComponent('Unable to save order', 'error')
    }
  }

  // On drag end
  const onDragEnd = (result) => {
    if (!result.destination) return

    const updatedOrder = reorder(players, result.source.index, result.destination.index)
    setPlayers(updatedOrder)

    savePlayerOrder(updatedOrder)
  }

  const removePlayer = (playerId) => {
    setPendingId(playerId)
    TeamPlayerService.deleteTeamPlayer(playerId, fixtureId)
      .then((res) => {
        if (res.status === 200) {
          toast.dismiss()
          ToastComponent(res.message, 'success')
          onChanged?.()
        }
      })
      .catch(() => ToastComponent('Unable to remove this player.', 'error'))
      .finally(() => setPendingId(null))
  }

  if (players.length === 0) {
    return (
      <div className="text-center text-medium-emphasis py-4">
        No players picked yet. Add players above to build this squad.
      </div>
    )
  }

  return (
    <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="playersTable">
          {(provided) => (
            <CTable hover responsive className="align-middle mb-0">
              <CTableHead>
                <CTableRow>
                  <CTableHeaderCell scope="col" style={{ width: '33.33%' }}>
                    Order
                  </CTableHeaderCell>
                  <CTableHeaderCell scope="col" style={{ width: '33.33%' }}>
                    Player Name
                  </CTableHeaderCell>
                  <CTableHeaderCell scope="col" style={{ width: '33.33%' }}>
                    Remove
                  </CTableHeaderCell>
                </CTableRow>
              </CTableHead>

              <CTableBody {...provided.droppableProps} ref={provided.innerRef}>
                {players.map((player, index) => (
                  <Draggable key={player.id} draggableId={player.id.toString()} index={index}>
                    {(providedDrag, snapshot) => (
                      <CTableRow
                        ref={providedDrag.innerRef}
                        {...providedDrag.draggableProps}
                        style={{
                          ...providedDrag.draggableProps.style,
                          background: snapshot.isDragging ? 'var(--cui-tertiary-bg)' : undefined,
                        }}
                      >
                        <CTableDataCell>
                          <span
                            {...providedDrag.dragHandleProps}
                            className="d-inline-flex align-items-center gap-2 text-medium-emphasis"
                            style={{ cursor: 'grab' }}
                            title="Drag to reorder"
                            aria-label={`Reorder ${player.player_name}`}
                          >
                            <GripVertical size={16} />
                            <span className="fw-semibold text-body">{index + 1}</span>
                          </span>
                        </CTableDataCell>
                        <CTableDataCell className="fw-semibold">
                          {player.player_name}
                        </CTableDataCell>
                        <CTableDataCell>
                          <CButton
                            size="sm"
                            color="danger"
                            variant="outline"
                            className="ge-row-icon-btn"
                            disabled={pendingId === player.player_id}
                            title="Remove from squad"
                            aria-label={`Remove ${player.player_name} from squad`}
                            onClick={() => removePlayer(player.player_id)}
                          >
                            {pendingId === player.player_id ? (
                              <CSpinner size="sm" />
                            ) : (
                              <CIcon icon={cilMinus} />
                            )}
                          </CButton>
                        </CTableDataCell>
                      </CTableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </CTableBody>
            </CTable>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  )
}

export default SquadPickedTable
